// src/pages/system/PaymentGatewayConfig.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Switch,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Chip,
  Alert,
  Tooltip,
  Snackbar,
  Avatar,
  Divider,
  Card,
  CardContent,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Payment as PaymentIcon,
  Info as InfoIcon,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  CreditCard as CreditCardIcon,
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon
} from '@mui/icons-material';

// Lista predefinida de pasarelas para soporte ampliado
const GATEWAY_CATALOG = [
  {
    type: 'mercadopago',
    name: 'Mercado Pago',
    subtitle: 'Cobros con Tarjetas, Yape, Efectivo y Código QR en Perú / LatAm',
    iconColor: '#009EE3',
    iconBg: 'rgba(0, 158, 227, 0.1)',
    docsUrl: 'https://www.mercadopago.com.pe/developers'
  },
  {
    type: 'culqi',
    name: 'Culqi',
    subtitle: 'Pasarela peruana para tarjetas de crédito/débito, Yape y PagoEfectivo',
    iconColor: '#F59E0B',
    iconBg: 'rgba(245, 158, 11, 0.12)',
    docsUrl: 'https://culqipanel.culqi.com/development/apikeys?storeId=200000000306850&productId=G'
  },
  {
    type: 'openpay',
    name: 'Openpay (BBVA)',
    subtitle: 'Pasarela de BBVA para Tarjetas, Yape, PagoEfectivo y Banca Móvil en Perú',
    iconColor: '#004481',
    iconBg: 'rgba(0, 68, 129, 0.12)',
    docsUrl: 'https://dashboard.openpay.pe/'
  },
  {
    type: 'izipay',
    name: 'Izipay / Izipay Online',
    subtitle: 'Cobros mediante tarjeta de crédito, débito y Yape en Perú',
    iconColor: '#E11D48',
    iconBg: 'rgba(225, 29, 72, 0.1)',
    comingSoon: true
  },
  {
    type: 'flow',
    name: 'Flow',
    subtitle: 'Pasarela de pagos en línea para tarjetas y medios locales',
    iconColor: '#0EA5E9',
    iconBg: 'rgba(14, 165, 233, 0.1)',
    comingSoon: true
  },
  {
    type: 'stripe',
    name: 'Stripe Global',
    subtitle: 'Procesamiento internacional de tarjetas de crédito y débito',
    iconColor: '#6366F1',
    iconBg: 'rgba(99, 102, 241, 0.1)',
    comingSoon: true
  },
  {
    type: 'paypal',
    name: 'PayPal',
    subtitle: 'Cobros globales con saldo PayPal y tarjetas de crédito',
    iconColor: '#003087',
    iconBg: 'rgba(0, 48, 135, 0.1)',
    comingSoon: true
  }
];

function PaymentGatewayConfig({ showToast: propShowToast }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [gateways, setGateways] = useState([]);
  const [showTokens, setShowTokens] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [copiedWebhook, setCopiedWebhook] = useState({});

  // Toast local fallback si no viene por prop
  const [localToast, setLocalToast] = useState({ open: false, message: '', severity: 'info' });

  const showToast = (message, severity = 'info') => {
    if (propShowToast) {
      propShowToast(message, severity);
    } else {
      setLocalToast({ open: true, message, severity });
    }
  };

  useEffect(() => {
    fetchGateways();
  }, []);

  const fetchGateways = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/finance/payment-gateways', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const normalized = data.map(g => {
          const isSandbox = !!g.sandbox_mode;
          return {
            ...g,
            public_key: isSandbox ? (g.test_public_key || g.public_key || '') : (g.live_public_key || g.public_key || ''),
            access_token: isSandbox ? (g.test_access_token || g.access_token || '') : (g.live_access_token || g.access_token || ''),
            client_id: isSandbox ? (g.test_client_id || g.client_id || '') : (g.live_client_id || g.client_id || '')
          };
        });
        setGateways(normalized);
      } else {
        showToast('Error al obtener la configuración de pasarelas', 'error');
      }
    } catch (error) {
      console.error('Error fetching gateways:', error);
      showToast('Error de red al obtener pasarelas de pago', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = (id) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const handleToggleChange = (id, field) => {
    setGateways(prev =>
      prev.map(g => {
        if (g.id !== id) return g;
        
        if (field === 'sandbox_mode') {
          const newSandbox = !g.sandbox_mode;
          
          // Preservar valores actuales antes de alternar
          const currentTestPk = g.sandbox_mode ? (g.public_key || g.test_public_key) : g.test_public_key;
          const currentTestToken = g.sandbox_mode ? (g.access_token || g.test_access_token) : g.test_access_token;
          const currentTestClientId = g.sandbox_mode ? (g.client_id || g.test_client_id) : g.test_client_id;
          
          const currentLivePk = !g.sandbox_mode ? (g.public_key || g.live_public_key) : g.live_public_key;
          const currentLiveToken = !g.sandbox_mode ? (g.access_token || g.live_access_token) : g.live_access_token;
          const currentLiveClientId = !g.sandbox_mode ? (g.client_id || g.live_client_id) : g.live_client_id;

          const nextPk = newSandbox ? (currentTestPk || '') : (currentLivePk || '');
          const nextToken = newSandbox ? (currentTestToken || '') : (currentLiveToken || '');
          const nextClientId = newSandbox ? (currentTestClientId || '') : (currentLiveClientId || '');

          return {
            ...g,
            sandbox_mode: newSandbox,
            test_public_key: currentTestPk,
            test_access_token: currentTestToken,
            test_client_id: currentTestClientId,
            live_public_key: currentLivePk,
            live_access_token: currentLiveToken,
            live_client_id: currentLiveClientId,
            public_key: nextPk,
            access_token: nextToken,
            client_id: nextClientId
          };
        }

        return { ...g, [field]: !g[field] };
      })
    );
  };

  const handleFieldChange = (id, field, value) => {
    setGateways(prev =>
      prev.map(g => {
        if (g.id !== id) return g;

        const updated = { ...g, [field]: value };
        if (field === 'public_key') {
          if (g.sandbox_mode) updated.test_public_key = value;
          else updated.live_public_key = value;
        } else if (field === 'access_token') {
          if (g.sandbox_mode) updated.test_access_token = value;
          else updated.live_access_token = value;
        } else if (field === 'client_id') {
          if (g.sandbox_mode) updated.test_client_id = value;
          else updated.live_client_id = value;
        }

        return updated;
      })
    );
  };

  const handleToggleShowToken = (id) => {
    setShowTokens(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSaveGateway = async (gateway) => {
    setSaving(prev => ({ ...prev, [gateway.id]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/finance/payment-gateways/${gateway.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          description: gateway.description,
          visible_to_client: gateway.visible_to_client,
          sandbox_mode: gateway.sandbox_mode,
          public_key: gateway.public_key,
          access_token: gateway.access_token,
          client_id: gateway.client_id,
          client_secret: gateway.client_secret,
          test_public_key: gateway.test_public_key,
          test_access_token: gateway.test_access_token,
          test_client_id: gateway.test_client_id,
          test_client_secret: gateway.test_client_secret,
          live_public_key: gateway.live_public_key,
          live_access_token: gateway.live_access_token,
          live_client_id: gateway.live_client_id,
          live_client_secret: gateway.live_client_secret,
          financial_account_id: gateway.financial_account_id
        })
      });

      if (response.ok) {
        showToast(`Pasarela ${gateway.name} actualizada correctamente`, 'success');
        fetchGateways();
      } else {
        showToast(`Error al guardar configuración de ${gateway.name}`, 'error');
      }
    } catch (error) {
      console.error('Error saving gateway:', error);
      showToast('Error de red al guardar configuración', 'error');
    } finally {
      setSaving(prev => ({ ...prev, [gateway.id]: false }));
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2}>
        <CircularProgress size={45} thickness={4} />
        <Typography variant="body2" color="textSecondary" fontWeight={600}>
          Cargando pasarelas de pago...
        </Typography>
      </Box>
    );
  }

  // Fusionar catálogo con los datos de base de datos
  const allGatewaysList = GATEWAY_CATALOG.map(cat => {
    const dbGateway = gateways.find(g => g.gateway_type === cat.type);
    if (dbGateway) {
      return { ...cat, ...dbGateway, configuredInDb: true };
    }
    return { ...cat, configuredInDb: false };
  });

  const activeCount = gateways.filter(g => g.visible_to_client).length;
  const configuredCount = gateways.filter(g => g.public_key || g.access_token).length;

  // Renderizador de formulario interno (reutilizado tanto en Mobile como en Desktop)
  const renderGatewayForm = (gateway) => (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2.5}>
        <Typography variant="subtitle1" fontWeight={700} color="#0f172a" fontSize="0.98rem">
          Credenciales y Ajustes: {gateway.name}
        </Typography>

        {gateway.docsUrl && (
          <Button
            variant="outlined"
            size="small"
            href={gateway.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<InfoIcon fontSize="small" />}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, fontSize: '0.78rem' }}
          >
            Obtener Llaves
          </Button>
        )}
      </Box>

      <Alert
        severity={gateway.sandbox_mode ? 'warning' : 'success'}
        sx={{ mb: 2.5, borderRadius: 2, border: '1px solid', borderColor: gateway.sandbox_mode ? '#fde68a' : '#a7f3d0' }}
      >
        {gateway.sandbox_mode ? (
          <Typography variant="body2" fontSize="0.82rem">
            <strong>MODO SANDBOX (Pruebas) ACTIVO:</strong> Las transacciones procesadas con {gateway.name} serán ficticias. No se cobrará dinero real a los clientes.
          </Typography>
        ) : (
          <Typography variant="body2" fontSize="0.82rem">
            <strong>MODO PRODUCCIÓN ACTIVO:</strong> Los cobros procesarán dinero real directamente hacia tu cuenta en {gateway.name}.
          </Typography>
        )}
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Descripción mostrada al cliente en el portal"
            value={gateway.description || ''}
            onChange={(e) => handleFieldChange(gateway.id, 'description', e.target.value)}
            placeholder="Ej: Tarjeta de crédito/débito, Yape o PagoEfectivo"
            helperText="Mensaje corto mostrado bajo el nombre del método en el portal."
            size="small"
          />
        </Grid>

        {gateway.gateway_type === 'openpay' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={gateway.sandbox_mode ? 'Merchant ID (Prueba)' : 'Merchant ID (Producción)'}
              value={gateway.client_id || ''}
              onChange={(e) => handleFieldChange(gateway.id, 'client_id', e.target.value)}
              placeholder="Ej: m0123456789abcdef"
              helperText="Identificador único de tu comercio asignado en el panel de Openpay."
              size="small"
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={gateway.sandbox_mode ? 'Public Key (Prueba / Sandbox)' : 'Public Key (Producción / Live)'}
            value={gateway.public_key || ''}
            onChange={(e) => handleFieldChange(gateway.id, 'public_key', e.target.value)}
            placeholder={gateway.sandbox_mode 
              ? (gateway.gateway_type === 'culqi' ? 'pk_test_...' : 'TEST-...') 
              : (gateway.gateway_type === 'culqi' ? 'pk_live_...' : 'APP_USR-...')}
            helperText={gateway.sandbox_mode 
              ? 'Llave pública de pruebas (sandbox). Se usará para transacciones ficticias.' 
              : 'Llave pública de producción. Se usará para cobros reales.'}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Chip 
                    label={gateway.sandbox_mode ? 'TEST' : 'LIVE'} 
                    size="small" 
                    color={gateway.sandbox_mode ? 'warning' : 'success'}
                    sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }} 
                  />
                </InputAdornment>
              )
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type={showTokens[gateway.id] ? 'text' : 'password'}
            label={gateway.sandbox_mode ? 'Secret Key (Prueba / Sandbox)' : 'Secret Key (Producción / Live)'}
            value={gateway.access_token || ''}
            onChange={(e) => handleFieldChange(gateway.id, 'access_token', e.target.value)}
            placeholder={gateway.sandbox_mode 
              ? (gateway.gateway_type === 'culqi' ? 'sk_test_...' : 'TEST-...') 
              : (gateway.gateway_type === 'culqi' ? 'sk_live_...' : 'APP_USR-...')}
            helperText={gateway.sandbox_mode 
              ? 'Llave privada de pruebas (sandbox).' 
              : 'Llave privada de producción. Confidencial.'}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Chip 
                    label={gateway.sandbox_mode ? 'TEST' : 'LIVE'} 
                    size="small" 
                    color={gateway.sandbox_mode ? 'warning' : 'success'}
                    sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }} 
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleToggleShowToken(gateway.id)} edge="end" size="small">
                    {showTokens[gateway.id] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Grid>

        {/* Código de Verificación de Webhook para Openpay (BBVA) */}
        {gateway.gateway_type === 'openpay' && (
          <Grid item xs={12}>
            <Alert 
              severity={gateway.webhook_verification_code ? "info" : "warning"}
              sx={{ 
                borderRadius: 2.5, 
                border: '1px solid',
                borderColor: gateway.webhook_verification_code ? '#93c5fd' : '#fde68a',
                bgcolor: gateway.webhook_verification_code ? 'rgba(239, 246, 255, 0.9)' : '#fffbeb'
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={800} color="#1e40af" display="flex" alignItems="center" gap={1}>
                    🔑 Código de Verificación Webhook de Openpay
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {gateway.webhook_verification_code 
                      ? 'Copie este código e ingréselo en la ventana de verificación del panel de Openpay para activar los webhooks.'
                      : 'Al agregar la URL de Webhook en Openpay, el código enviado por Openpay aparecerá aquí automáticamente para copiar.'}
                  </Typography>
                  {gateway.webhook_verification_code && (
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 900, fontSize: '1.1rem', color: '#1e3a8a', mt: 0.5, letterSpacing: '1px' }}>
                      {gateway.webhook_verification_code}
                    </Typography>
                  )}
                </Box>
                {gateway.webhook_verification_code && (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      navigator.clipboard.writeText(gateway.webhook_verification_code);
                      showToast('¡Código de verificación copiado al portapapeles!', 'success');
                    }}
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 2 }}
                  >
                    Copiar Código
                  </Button>
                )}
              </Box>
            </Alert>
          </Grid>
        )}

        {/* URL de Webhook para notificaciones de la pasarela */}
        <Grid item xs={12}>
          <Box sx={{ p: 2, bgcolor: '#ffffff', borderRadius: 2.5, border: '1px solid #cbd5e1', mt: 1 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                  🔗 URL de Webhook / Notificaciones Asíncronas
                </Typography>
                <Chip label="Copiar e ingresar en tu panel de pasarela" size="small" color="info" variant="outlined" sx={{ fontSize: '0.7rem', fontWeight: 600 }} />
              </Box>
            </Box>

            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1.5 }}>
              Ingresa esta dirección URL de Webhook en la sección de Webhooks/Notificaciones del panel oficial de <strong>{gateway.name}</strong> para que la activación de pagos y reconexión MikroTik sea automática e instantánea.
            </Typography>

            <TextField
              fullWidth
              readOnly
              size="small"
              value={`${window.location.origin}/api/client/payments/online/webhook/${gateway.type}`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>POST</Typography>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      variant={copiedWebhook[gateway.type] ? "contained" : "outlined"}
                      color={copiedWebhook[gateway.type] ? "success" : "primary"}
                      onClick={() => {
                        const webhookUrl = `${window.location.origin}/api/client/payments/online/webhook/${gateway.type}`;
                        navigator.clipboard.writeText(webhookUrl);
                        setCopiedWebhook(prev => ({ ...prev, [gateway.type]: true }));
                        showToast(`¡URL Webhook de ${gateway.name} copiada!`, 'success');
                        setTimeout(() => setCopiedWebhook(prev => ({ ...prev, [gateway.type]: false })), 3000);
                      }}
                      startIcon={copiedWebhook[gateway.type] ? <CheckIcon /> : <ContentCopyIcon fontSize="small" />}
                      sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, fontSize: '0.75rem' }}
                    >
                      {copiedWebhook[gateway.type] ? '¡Copiado!' : 'Copiar URL Webhook'}
                    </Button>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#f1f5f9',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem'
                }
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2.5 }} />

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          onClick={() => handleSaveGateway(gateway)}
          disabled={saving[gateway.id]}
          startIcon={saving[gateway.id] ? <CircularProgress size={16} color="inherit" /> : <SaveIcon fontSize="small" />}
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.875rem',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
          }}
        >
          {saving[gateway.id] ? 'Guardando...' : `Guardar Credenciales`}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Cabecera Principal */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 3.5 },
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#ffffff',
          boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.15)'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                width: { xs: 44, sm: 54 },
                height: { xs: 44, sm: 54 },
                bgcolor: 'rgba(52, 211, 153, 0.15)',
                color: '#34d399',
                border: '1px solid rgba(52, 211, 153, 0.3)'
              }}
            >
              <PaymentIcon sx={{ fontSize: { xs: 24, sm: 30 } }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: '-0.02em', color: '#f8fafc', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Pasarelas de Pago Online
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Configura cobros electrónicos automáticos con reconexión inmediata de clientes.
              </Typography>
            </Box>
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              icon={<CheckIcon sx={{ fontSize: '15px !important', color: '#34d399 !important' }} />}
              label={`${activeCount} Activa(s)`}
              size="small"
              sx={{
                bgcolor: 'rgba(52, 211, 153, 0.12)',
                color: '#34d399',
                fontWeight: 600,
                fontSize: '0.78rem',
                border: '1px solid rgba(52, 211, 153, 0.2)'
              }}
            />
            <Chip
              icon={<SettingsIcon sx={{ fontSize: '15px !important', color: '#60a5fa !important' }} />}
              label={`${configuredCount} Configurada(s)`}
              size="small"
              sx={{
                bgcolor: 'rgba(96, 165, 250, 0.12)',
                color: '#60a5fa',
                fontWeight: 600,
                fontSize: '0.78rem',
                border: '1px solid rgba(96, 165, 250, 0.2)'
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* VISTA MÓVIL (TARJETAS APILADAS RESPONSIVAS) */}
      {isMobile ? (
        <Box display="flex" flexDirection="column" gap={2}>
          {allGatewaysList.map((gateway) => {
            const isExpanded = expandedId === gateway.id;
            const isConfigured = Boolean(gateway.public_key || gateway.access_token);
            const isComingSoon = gateway.comingSoon;

            return (
              <Card
                key={gateway.type}
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
                  overflow: 'hidden'
                }}
              >
                {/* Cabecera Móvil */}
                <Box
                  p={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  bgcolor={isExpanded ? '#f1f5f9' : '#ffffff'}
                  onClick={() => {
                    if (!isComingSoon && gateway.configuredInDb) {
                      handleToggleExpand(gateway.id);
                    }
                  }}
                  sx={{ cursor: isComingSoon ? 'default' : 'pointer' }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: gateway.iconBg,
                        color: gateway.iconColor,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        border: `1px solid ${gateway.iconColor}33`
                      }}
                    >
                      <CreditCardIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2" fontWeight={700} color="#1e293b">
                          {gateway.name}
                        </Typography>
                        {isComingSoon && (
                          <Chip label="Próximamente" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.72rem', display: 'block' }}>
                        {gateway.subtitle}
                      </Typography>
                    </Box>
                  </Box>

                  {!isComingSoon && gateway.configuredInDb && (
                    <IconButton size="small" color={isExpanded ? 'primary' : 'default'}>
                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  )}
                </Box>

                {/* Toggles y Estados Rápidos Móviles */}
                {!isComingSoon && gateway.configuredInDb && (
                  <>
                    <Divider />
                    <Box p={1.5} px={2} display="flex" flexDirection="column" gap={1} bgcolor="#fafafa">
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" fontWeight={600} color="textSecondary">
                          Visible en Portal Clientes:
                        </Typography>
                        <Switch
                          size="small"
                          checked={Boolean(gateway.visible_to_client)}
                          onChange={() => handleToggleChange(gateway.id, 'visible_to_client')}
                          color="primary"
                        />
                      </Box>

                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" fontWeight={600} color="textSecondary">
                          Modo de Entorno:
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={Boolean(gateway.sandbox_mode)}
                              onChange={() => handleToggleChange(gateway.id, 'sandbox_mode')}
                              color="warning"
                            />
                          }
                          label={
                            <Chip
                              label={gateway.sandbox_mode ? 'Sandbox (Pruebas)' : 'Producción (Real)'}
                              size="small"
                              color={gateway.sandbox_mode ? 'warning' : 'primary'}
                              sx={{ fontWeight: 700, fontSize: '0.68rem', height: 22 }}
                            />
                          }
                          labelPlacement="start"
                          sx={{ m: 0 }}
                        />
                      </Box>

                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" fontWeight={600} color="textSecondary">
                          Estado Credenciales:
                        </Typography>
                        <Chip
                          icon={isConfigured ? <CheckIcon fontSize="small" /> : <WarningIcon fontSize="small" />}
                          label={isConfigured ? 'Configurado' : 'Sin Credenciales'}
                          size="small"
                          sx={{
                            bgcolor: isConfigured ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                            color: isConfigured ? '#047857' : '#b45309',
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            height: 22
                          }}
                        />
                      </Box>
                    </Box>
                  </>
                )}

                {/* Formulario desplegable en Móvil */}
                {!isComingSoon && gateway.configuredInDb && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    {renderGatewayForm(gateway)}
                  </Collapse>
                )}
              </Card>
            );
          })}
        </Box>
      ) : (
        /* VISTA DESKTOP / TABLET (TABLA EXPANDIBLE SCROLLABLE) */
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            overflowX: 'auto',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}
        >
          <Table sx={{ minWidth: 720 }}>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem' }}>
                  PASARELA DE PAGO
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem' }}>
                  VISIBILIDAD PORTAL
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem' }}>
                  ENTORNO DE EMISIÓN
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem' }}>
                  CREDENCIALES
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem' }}>
                  URL WEBHOOK
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', pr: 3 }}>
                  DETALLES
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allGatewaysList.map((gateway) => {
                const isExpanded = expandedId === gateway.id;
                const isConfigured = Boolean(gateway.public_key || gateway.access_token);
                const isComingSoon = gateway.comingSoon;

                return (
                  <React.Fragment key={gateway.type}>
                    {/* Fila Principal de la Tabla */}
                    <TableRow
                      onClick={() => {
                        if (!isComingSoon && gateway.configuredInDb) {
                          handleToggleExpand(gateway.id);
                        }
                      }}
                      sx={{
                        cursor: isComingSoon ? 'default' : 'pointer',
                        bgcolor: isExpanded ? '#f1f5f9' : 'inherit',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          bgcolor: isComingSoon ? 'inherit' : '#f8fafc'
                        }
                      }}
                    >
                      {/* Pasarela e Ícono */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: gateway.iconBg,
                              color: gateway.iconColor,
                              borderRadius: 2.5,
                              fontWeight: 'bold',
                              border: `1px solid ${gateway.iconColor}33`
                            }}
                          >
                            <CreditCardIcon />
                          </Avatar>
                          <Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="subtitle1" fontWeight={700} color="#1e293b">
                                {gateway.name}
                              </Typography>
                              {isComingSoon && (
                                <Chip
                                  label="Próximamente"
                                  size="small"
                                  sx={{
                                    bgcolor: '#f1f5f9',
                                    color: '#64748b',
                                    fontSize: '0.7rem',
                                    fontWeight: 700
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.2 }}>
                              {gateway.subtitle}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Visibilidad Portal Clientes */}
                      <TableCell align="center">
                        {!isComingSoon && gateway.configuredInDb ? (
                          <FormControlLabel
                            onClick={(e) => e.stopPropagation()}
                            control={
                              <Switch
                                size="small"
                                checked={Boolean(gateway.visible_to_client)}
                                onChange={() => handleToggleChange(gateway.id, 'visible_to_client')}
                                color="primary"
                              />
                            }
                            label={
                              <Chip
                                label={gateway.visible_to_client ? 'Visible' : 'Oculto'}
                                size="small"
                                color={gateway.visible_to_client ? 'success' : 'default'}
                                variant={gateway.visible_to_client ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}
                              />
                            }
                          />
                        ) : (
                          <Typography variant="caption" color="textDisabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Entorno / Sandbox */}
                      <TableCell align="center">
                        {!isComingSoon && gateway.configuredInDb ? (
                          <FormControlLabel
                            onClick={(e) => e.stopPropagation()}
                            control={
                              <Switch
                                size="small"
                                checked={Boolean(gateway.sandbox_mode)}
                                onChange={() => handleToggleChange(gateway.id, 'sandbox_mode')}
                                color="warning"
                              />
                            }
                            label={
                              <Chip
                                label={gateway.sandbox_mode ? 'Sandbox (Pruebas)' : 'Producción (Real)'}
                                size="small"
                                color={gateway.sandbox_mode ? 'warning' : 'primary'}
                                sx={{ fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}
                              />
                            }
                          />
                        ) : (
                          <Typography variant="caption" color="textDisabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Estado de Credenciales */}
                      <TableCell align="center">
                        {!isComingSoon && gateway.configuredInDb ? (
                          <Chip
                            icon={isConfigured ? <CheckIcon fontSize="small" /> : <WarningIcon fontSize="small" />}
                            label={isConfigured ? 'Configurado' : 'Sin Credenciales'}
                            size="small"
                            sx={{
                              bgcolor: isConfigured ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                              color: isConfigured ? '#047857' : '#b45309',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              border: `1px solid ${isConfigured ? '#a7f3d0' : '#fde68a'}`
                            }}
                          />
                        ) : (
                          <Chip
                            icon={<LockIcon fontSize="small" />}
                            label="En desarrollo"
                            size="small"
                            variant="outlined"
                            sx={{ color: '#94a3b8', fontSize: '0.75rem' }}
                          />
                        )}
                      </TableCell>

                      {/* URL Webhook */}
                      <TableCell align="center">
                        {!isComingSoon && gateway.configuredInDb ? (
                          <Tooltip title={`${window.location.origin}/api/client/payments/online/webhook/${gateway.type}`}>
                            <Chip
                              icon={copiedWebhook[gateway.type] ? <CheckIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
                              label={copiedWebhook[gateway.type] ? '¡Copiado!' : 'Copiar Webhook'}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                const webhookUrl = `${window.location.origin}/api/client/payments/online/webhook/${gateway.type}`;
                                navigator.clipboard.writeText(webhookUrl);
                                setCopiedWebhook(prev => ({ ...prev, [gateway.type]: true }));
                                showToast(`¡URL Webhook de ${gateway.name} copiada!`, 'success');
                                setTimeout(() => setCopiedWebhook(prev => ({ ...prev, [gateway.type]: false })), 3000);
                              }}
                              color={copiedWebhook[gateway.type] ? 'success' : 'info'}
                              variant="outlined"
                              sx={{ fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="textDisabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Botón Acción Desplegar */}
                      <TableCell align="right" sx={{ pr: 3 }}>
                        {!isComingSoon && gateway.configuredInDb ? (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleExpand(gateway.id);
                            }}
                            sx={{
                              bgcolor: isExpanded ? '#3b82f6' : 'rgba(241, 245, 249, 0.8)',
                              color: isExpanded ? '#ffffff' : '#64748b',
                              '&:hover': {
                                bgcolor: isExpanded ? '#2563eb' : '#e2e8f0'
                              }
                            }}
                          >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        ) : (
                          <IconButton size="small" disabled>
                            <LockIcon fontSize="small" sx={{ color: '#cbd5e1' }} />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Fila Desplegable (Detalles del Formulario) */}
                    {!isComingSoon && gateway.configuredInDb && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ p: 0, borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {renderGatewayForm(gateway)}
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Snackbar local fallback */}
      <Snackbar
        open={localToast.open}
        autoHideDuration={5000}
        onClose={() => setLocalToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setLocalToast(prev => ({ ...prev, open: false }))}
          severity={localToast.severity}
          variant="filled"
          sx={{ borderRadius: 2.5 }}
        >
          {localToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PaymentGatewayConfig;
